// Compose and Comments UI Components
import { createPost, addComment, getComments, deleteComment } from '../db.js';
import { getCurrentUser } from '../auth.js';
import { loadFeed } from './feed.js';

/**
 * Initialize compose form
 */
export function initCompose(formElement) {
    formElement.addEventListener('submit', handleSubmit);
}

/**
 * Handle post submission
 */
async function handleSubmit(event) {
    event.preventDefault();

    const currentUser = getCurrentUser();
    if (!currentUser) {
        alert('Vous devez être connecté pour publier');
        return;
    }

    const form = event.target;
    const submitBtn = form.querySelector('button[type="submit"]');
    const text = form.querySelector('#post-text').value.trim();
    const book = form.querySelector('#post-book')?.value || '';
    const chapter = form.querySelector('#post-chapter')?.value || '';
    const verseStart = form.querySelector('#post-verse-start')?.value || '';
    const verseEnd = form.querySelector('#post-verse-end')?.value || '';

    if (!book || !verseStart) {
        alert('Veuillez sélectionner un verset avant de publier');
        return;
    }

    if (!text) {
        alert('Veuillez écrire quelque chose');
        return;
    }

    try {
        submitBtn.disabled = true;
        submitBtn.textContent = '⏳ Publication...';

        // Determine author's post language from localStorage or user profile
        let detectedLang = localStorage.getItem('user_preferred_lang') || null;
        if (!detectedLang) {
            try {
                const dbMod = await import('../db.js');
                detectedLang = await dbMod.getUserLanguage();
            } catch (e) {
                // ignore
            }
        }

        const postData = {
            text,
            language: detectedLang || 'french',
        };

        // Add verse reference if provided
        if (book && chapter) {
            postData.book = book;
            postData.chapter = parseInt(chapter);
            if (verseStart) postData.verseStart = parseInt(verseStart);
            if (verseEnd) postData.verseEnd = parseInt(verseEnd);
        }

        await createPost(postData, currentUser.uid);

        // Reset form
        form.reset();
        const selectedVerseDisplay = document.querySelector('#selected-verse-display');
        if (selectedVerseDisplay) selectedVerseDisplay.style.display = 'none';

        // Reload feed
        loadFeed();

        // Show success message
        showNotification('✅ Post publié avec succès !');
    } catch (error) {
        console.error('Error creating post:', error);
        alert('Erreur lors de la publication: ' + error.message);
    } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = '📤 Publier';
    }
}

/**
 * Show comments modal
 */
export async function showComments(post) {
    const currentUser = getCurrentUser();

    // Create modal
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.innerHTML = `
    <div class="modal-content">
      <div class="modal-header">
        <h3>💬 Commentaires</h3>
        <button class="close-btn">&times;</button>
      </div>
      <div class="modal-body">
        <div class="comments-list">
          <div class="loading">⏳ Chargement...</div>
        </div>
        ${currentUser ? `
          <form class="comment-form">
            <textarea 
              id="comment-text" 
              placeholder="Écrire un commentaire..." 
              rows="3"
              required
            ></textarea>
            <button type="submit" class="btn btn-primary">💬 Commenter</button>
          </form>
        ` : '<p class="auth-required">Connectez-vous pour commenter</p>'}
      </div>
    </div>
  `;

    document.body.appendChild(modal);

    // Close button
    modal.querySelector('.close-btn').addEventListener('click', () => {
        modal.remove();
    });

    // Click outside to close
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            modal.remove();
        }
    });

    // Load comments
    const commentsList = modal.querySelector('.comments-list');
    try {
        const comments = await getComments(post.id);

        if (comments.length === 0) {
            commentsList.innerHTML = '<p class="empty-comments">Aucun commentaire pour le moment</p>';
        } else {
            commentsList.innerHTML = '';
            comments.forEach(comment => {
                const commentEl = createCommentElement(comment, post.id);
                commentsList.appendChild(commentEl);
            });
        }
    } catch (error) {
        console.error('Error loading comments:', error);
        commentsList.innerHTML = '<p class="error">Erreur de chargement des commentaires</p>';
    }

    // Comment form submission
    if (currentUser) {
        const commentForm = modal.querySelector('.comment-form');
        commentForm.addEventListener('submit', async (e) => {
            e.preventDefault();

            const textarea = commentForm.querySelector('#comment-text');
            const text = textarea.value.trim();

            if (!text) return;

            try {
                const submitBtn = commentForm.querySelector('button[type="submit"]');
                submitBtn.disabled = true;
                submitBtn.textContent = '⏳ Envoi...';

                await addComment(post.id, text, currentUser.uid);

                // Reload comments
                const updatedComments = await getComments(post.id);
                commentsList.innerHTML = '';
                updatedComments.forEach(comment => {
                    const commentEl = createCommentElement(comment, post.id);
                    commentsList.appendChild(commentEl);
                });

                // Update comment count in feed
                const postCard = document.querySelector(`[data-post-id="${post.id}"]`);
                if (postCard) {
                    const countEl = postCard.querySelector('[data-action="comment"] .count');
                    if (countEl) {
                        countEl.textContent = updatedComments.length;
                    }
                }

                textarea.value = '';
                submitBtn.disabled = false;
                submitBtn.textContent = '💬 Commenter';
            } catch (error) {
                console.error('Error adding comment:', error);
                alert('Erreur lors de l\'ajout du commentaire');
            }
        });
    }
}

/**
 * Create comment element
 */
function createCommentElement(comment, postId) {
    const div = document.createElement('div');
    div.className = 'comment-item';
    div.dataset.commentId = comment.id;

    const currentUser = getCurrentUser();
    const isAuthor = currentUser && currentUser.uid === comment.authorId;

    const commentDate = comment.createdAt ? new Date(comment.createdAt.toDate()).toLocaleDateString('fr-FR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }) : '';

    div.innerHTML = `
    <img 
      src="${comment.author.photoURL || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(comment.author.displayName)}" 
      alt="${comment.author.displayName}"
      class="comment-avatar"
    />
    <div class="comment-content">
      <div class="comment-header">
        <span class="comment-author">${comment.author.displayName}</span>
        <span class="comment-date">${commentDate}</span>
        ${isAuthor ? '<button class="delete-comment-btn" data-action="delete-comment">🗑️</button>' : ''}
      </div>
      <div class="comment-text">${escapeHtml(comment.text)}</div>
    </div>
  `;

    // Delete button
    if (isAuthor) {
        const deleteBtn = div.querySelector('[data-action="delete-comment"]');
        deleteBtn.addEventListener('click', async () => {
            if (!confirm('Supprimer ce commentaire ?')) return;

            try {
                await deleteComment(postId, comment.id);
                div.remove();

                // Update count
                const postCard = document.querySelector(`[data-post-id="${postId}"]`);
                if (postCard) {
                    const countEl = postCard.querySelector('[data-action="comment"] .count');
                    if (countEl) {
                        countEl.textContent = Math.max(0, parseInt(countEl.textContent) - 1);
                    }
                }
            } catch (error) {
                console.error('Error deleting comment:', error);
                alert('Erreur lors de la suppression');
            }
        });
    }

    return div;
}

/**
 * Show notification
 */
function showNotification(message) {
    const notification = document.createElement('div');
    notification.className = 'notification';
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
        notification.classList.add('show');
    }, 100);

    setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => notification.remove(), 300);
    }, 3000);
}

/**
 * Escape HTML
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/\n/g, '<br>');
}
